import React, {useMemo, useState} from "react";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";
import InformationCircleIcon from "@heroicons/react/24/outline/InformationCircleIcon";
import PlusCircleIcon from "@heroicons/react/24/outline/PlusCircleIcon";
import MinusCircleIcon from "@heroicons/react/24/outline/MinusCircleIcon";
import {checkPerm, classNames, getProp} from '../../../../../common/util/util-helpers'
import {genericMoneyFormatter} from "../../../../../common/util/util-vanilla";
import {Field, FieldsManager as Fields, FieldsManager} from "../../../../../data/services/fields";
import {useDispatch, useSelector} from 'react-redux'
import LocalStorage from '../../../../../util/localStorage'
import Resources from '../../../../../data/services/resources'
import {EyeIcon} from '@heroicons/react/20/solid'
import {getInfoResource} from "../../../../../data/actions/infoResource";
import {showGlobalModal} from "../../../../../data/actions/ui";
import Tooltip from "../../../../../common/components/tooltip";
import Subtitle from "../../../../../common/components/layout/layout-components/page/subtitle";
import {LoaderLarge} from "../../../../../common/components/loader";
import TableCard from "../../../../../common/components/resource-table/table-components/table-card";
import ResourceTable from "../../../../../common/components/resource-table";
import ModalDefault from "../../../../../common/components/modal/modal-default";
import TableFooter from "../../../../../common/components/resource-table/table/table-footer";
import Pagination from "../../../../../common/components/resource-table/table-components/pagination";
import FieldSearch from "../../../../../common/components/fields/field-text/search";
import {fillFieldsFromData} from "../../../../../common/util/util-fields";
import {READ_PERM} from "../../../../../util/util-constants";
import Card from "../../../../../common/components/card";
import AdvancesDialog from "../../../../../common/components/modal/advances-modal";
import ResourceTableDialog from "../../../../../common/components/modal/resource-table-dialog";
import {DEFAULT_QUERY_LIMIT} from "../../../../../common/util/util-consts";

const ENTRY_DESCRIPTIONS = {
    UnpaidItemsTotal: "Total of truck pay lines from loads that are at least in dispatched status and have not yet been included in a payroll settlement.",
    EscrowBalancesTotal: "Running balance of funds held in escrow, accumulated through settlement transactions marked as Escrow accounts.",
    LoanBalancesTotal: "Total of all adjustments, bill charges, advances, and reimbursements that affect the truck's balance outside of regular pay.",
    DeductionsTotal: "Sum of all active one-time deductions that are not yet on the settlements."
}

const tableOptions = {
    behaviour: {
        hasVirtualRows: false
    },
    style: {
        condensed: true,
        horizontalLines: false,
        verticalLines: true,
        stripedRows: true
    }
}

export default function TruckPayrollOpenPayEntries({
                                                       data = {},
                                                       unpaidItemsFields,
                                                       escrowBalancesFields,
                                                       loanBalancesFields,
                                                       translate,
                                                       openPayEntryFields,
                                                       openPayEntryData,
                                                       isLoading,
                                                       TruckID,
                                                   }) {
    const dispatch = useDispatch();

    const [isSectionExpanded, setIsSectionExpanded] = useState(true);
    const [activeModal, setActiveModal] = useState();
    const [unpaidItemsFilterValue, setUnpaidItemsFilterValue] = useState("");
    const [escrowBalancesFilterValue, setEscrowBalancesFilterValue] = useState("");
    const [loanBalancesFilterValue, setLoanBalancesFilterValue] = useState("");

    const advancesResource = useSelector((state) => state.info);
    const dialogResource = useSelector((state) => state.dialogResource);

    const [isContactAdvancesDialogOpen, setIsContactAdvancesDialogOpen] = useState(false);
    const [isReimbursementsDialogOpen, setIsReimbursementsDialogOpen] = useState(false);
    const [isAdvancesDialogOpen, setIsAdvancesDialogOpen] = useState(false);
    const [isDeductionsDialogOpen, setIsDeductionsDialogOpen] = useState(false);

    const hasOpenEntries = !!data?.UnpaidItems?.length || !!data?.LoanBalances?.length || !!data?.EscrowBalances?.length;

    const [unpaidItemsQuery, setUnpaidItemsQuery] = useState(getQueryFields());
    const unpaidItemsQueryValues = Fields.getFieldKeyValues(unpaidItemsQuery);
    const handleUpdateUnpaidItemsOffset = (name, value) => {
        const unpaidItemsQueryUpdate = FieldsManager.updateField(unpaidItemsQuery, name, value);
        setUnpaidItemsQuery(unpaidItemsQueryUpdate);
    }

    const [escrowBalancesQuery, setEscrowBalancesQuery] = useState(getQueryFields());
    const escrowBalancesQueryValues = Fields.getFieldKeyValues(escrowBalancesQuery);
    const handleEscrowBalancesOffset = (name, value) => {
        const unpaidItemsQueryUpdate = FieldsManager.updateField(escrowBalancesQuery, name, value);
        setEscrowBalancesQuery(unpaidItemsQueryUpdate);
    }

    const [loanBalancesQuery, setLoanBalancesQuery] = useState(getQueryFields());
    const loanBalancesQueryValues = Fields.getFieldKeyValues(loanBalancesQuery);
    const handleLoanBalancesOffset = (name, value) => {
        const unpaidItemsQueryUpdate = FieldsManager.updateField(loanBalancesQuery, name, value);
        setLoanBalancesQuery(unpaidItemsQueryUpdate);
    }

    const tableData = useMemo(() => {
        const tableData = {};
        tableData.UnpaidItems = Object.assign({}, data?.UnpaidItems ?? {});
        if (tableData.UnpaidItems) {
            let fieldsList = Object.keys(unpaidItemsFields);
            tableData.UnpaidItems = Object.values(tableData.UnpaidItems).reduce((memo, row) => {

                Object.keys(row).forEach(key => {
                    if (!fieldsList.includes(key)) {
                        delete row[key];
                    }
                })

                memo.push(row)
                return memo;
            }, []);
        }

        tableData.EscrowBalances = Object.assign({}, data?.EscrowBalances ?? {});
        if (tableData.EscrowBalances) {
            let fieldsList = Object.keys(escrowBalancesFields);
            tableData.EscrowBalances = Object.values(tableData.EscrowBalances).reduce((memo, row) => {

                Object.keys(row).forEach(key => {
                    if (!fieldsList.includes(key)) {
                        delete row[key];
                    }
                })

                memo.push(row)
                return memo;
            }, []);
        }

        tableData.LoanBalances = Object.assign({}, data?.LoanBalances ?? {});
        if (tableData.LoanBalances) {
            let fieldsList = Object.keys(loanBalancesFields);
            tableData.LoanBalances = Object.values(tableData?.LoanBalances ?? {}).reduce((memo, row) => {

                Object.keys(row).forEach(key => {
                    if (!fieldsList.includes(key)) {
                        delete row[key];
                    }
                })

                memo.push(row)
                return memo;
            }, []);
        }

        return tableData;
    }, [data])


    let unpaidItemsTableData = unpaidItemsFilterValue
        ? tableData.UnpaidItems.filter(d => {
            return Object.values(Object.assign({}, d))
                .find(it => {
                    return typeof it === "string" ? it.toUpperCase().includes(unpaidItemsFilterValue.toUpperCase()) : it.toString().includes(unpaidItemsFilterValue)
                });
        })
        : tableData.UnpaidItems

    let paginatedUnpaidItemsTableData = [];
    if (unpaidItemsTableData?.length) {
        paginatedUnpaidItemsTableData = unpaidItemsTableData.slice(unpaidItemsQueryValues.offset, unpaidItemsQueryValues.limit);
    }

    let escrowBalancesTableData = escrowBalancesFilterValue
        ? tableData.EscrowBalances.filter(d => {
            return Object.values(Object.assign({}, d))
                .find(it => {
                    return typeof it === "string" ? it.toUpperCase().includes(escrowBalancesFilterValue.toUpperCase()) : it.toString().includes(escrowBalancesFilterValue)
                });
        })
        : tableData.EscrowBalances

    let paginatedEscrowBalancesTableData = [];
    if (escrowBalancesTableData?.length) {
        paginatedEscrowBalancesTableData = escrowBalancesTableData.slice(escrowBalancesQueryValues.offset, escrowBalancesQueryValues.limit) ?? [];
    }

    let loanBalancesTableData = loanBalancesFilterValue
        ? tableData.LoanBalances.filter(d => {
            return Object.values(Object.assign({}, d))
                .find(it => {
                    return typeof it === "string" ? it.toUpperCase().includes(loanBalancesFilterValue.toUpperCase()) : it.toString().includes(loanBalancesFilterValue)
                });
        })
        : tableData.LoanBalances

    let paginatedLoanBalancesTableData = [];
    if (loanBalancesTableData?.length) {
        paginatedLoanBalancesTableData = loanBalancesTableData.slice(loanBalancesQueryValues.offset, loanBalancesQueryValues.limit) ?? [];
    }

    const toggleTableModal = (rowData) => {
        if (rowData?.Item === 'DeductionsTotal') {
            setIsDeductionsDialogOpen(true);
            return;
        }
        setActiveModal(rowData?.Item);
    }

    const grandTotal = getProp(data, "UnpaidItemsTotal", 0)
        + getProp(data, "EscrowBalancesTotal", 0)
        - getProp(data, "LoanBalancesTotal", 0)
        - getProp(data, "DeductionsTotal", 0);

    function getQueryFields(item = {}) {
        const fieldTemplates = {
            offset: new Field('offset', 0, [''], false, ''),
            limit: new Field('limit', DEFAULT_QUERY_LIMIT, [''], false, 'select', {
                hideLabel: true,
                labelType: 'float'
            }),
        }

        return fillFieldsFromData(fieldTemplates, item);
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center">
                <button
                    className="btn btn-icon text-tm-gray-500 hover:text-tm-gray-700 w-8 h-8 -ml-2 md mr-2"
                    onClick={() => setIsSectionExpanded(!isSectionExpanded)}
                >
                    <ChevronRightIcon
                        className={
                            classNames(
                                "w-5 h-5",
                                isSectionExpanded ? 'rotate-90' : undefined
                            )
                        }
                    />
                </button>

                <Subtitle subtitle={translate("text.open_pay_entries")}/>
            </div>

            {isLoading && (
                <div className="relative h-48">
                    <LoaderLarge/>
                </div>
            )}

            {isSectionExpanded && !isLoading && (
                <React.Fragment>
                    {hasOpenEntries && (
                        <div className="w-1/2 min-w-80">

                            <TableCard addClass="overflow-hidden">
                                <table className="min-w-full">
                                    <tbody className="divide-y divide-tm-gray-200">
                                        {openPayEntryData.map((entry, index) => (
                                            <tr
                                                key={entry.Item}
                                                onClick={() => toggleTableModal(entry)}
                                                className={classNames(
                                                    "cursor-pointer hover:bg-tm-gray-100",
                                                    index % 2 === 0 ? "bg-inverse" : "bg-tm-gray-50"
                                                )}
                                            >
                                                <td className="px-4 py-2.5 text-sm text-tm-gray-900">
                                                    <div className="flex items-center gap-1.5">
                                                        <span>{entry.Name}</span>
                                                        <Tooltip
                                                            content={ENTRY_DESCRIPTIONS[entry.Item]}
                                                            maxWidth={300}
                                                        >
                                                            <span>
                                                                <InformationCircleIcon className="w-4 h-4 text-tm-gray-400 cursor-help shrink-0"/>
                                                            </span>
                                                        </Tooltip>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2.5 text-sm text-right font-medium text-tm-gray-900">
                                                    {genericMoneyFormatter(entry.TotalAmount)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </TableCard>

                            <div className="pt-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    {checkPerm(Resources.AccountingAccountAdvances, READ_PERM) && (
                                        <Tooltip
                                            content="Bill charges assigned to this truck that are not yet included in a posted payroll batch entry."
                                            maxWidth={260}
                                        >
                                            <button
                                                className="btn btn-outline flex items-center gap-1.5"
                                                onClick={() => setIsContactAdvancesDialogOpen(true)}
                                            >
                                                <EyeIcon className="w-4 h-4 shrink-0"/>
                                                Unprocessed bills
                                            </button>
                                        </Tooltip>
                                    )}

                                    <Tooltip
                                        content="Reimbursements owed to this truck that are not yet included in a posted payroll batch entry."
                                        maxWidth={260}
                                    >
                                        <button
                                            className={checkPerm(Resources.AccountingReimbursement, READ_PERM)
                                                ? "btn btn-outline flex items-center gap-1.5"
                                                : "focus:outline-hidden focus:ring-1 rounded-btn ring-red-600 p-2 text-tm-gray-500 cursor-default flex items-center gap-1.5"
                                            }
                                            onClick={() => checkPerm(Resources.AccountingReimbursement, READ_PERM)
                                                ? setIsReimbursementsDialogOpen(true)
                                                : dispatch(showGlobalModal('NoPermissionModal'))
                                            }
                                        >
                                            <EyeIcon className="w-4 h-4 shrink-0"/>
                                            Unprocessed reimbursements
                                        </button>
                                    </Tooltip>

                                    <Tooltip
                                        content="Advances issued to this truck that are not yet included in a posted payroll batch entry."
                                        maxWidth={260}
                                    >
                                        <button
                                            className={checkPerm(Resources.AccountingReimbursement, READ_PERM)
                                                ? "btn btn-outline flex items-center gap-1.5"
                                                : "focus:outline-hidden focus:ring-1 rounded-btn ring-red-600 p-2 text-tm-gray-500 cursor-default flex items-center gap-1.5"
                                            }
                                            onClick={() => checkPerm(Resources.AccountingReimbursement, READ_PERM)
                                                ? setIsAdvancesDialogOpen(true)
                                                : dispatch(showGlobalModal('NoPermissionModal'))
                                            }
                                        >
                                            <EyeIcon className="w-4 h-4 shrink-0"/>
                                            Unprocessed advances
                                        </button>
                                    </Tooltip>

                                    <Tooltip
                                        content="Active one-time deductions assigned to this truck that are not yet included in a posted payroll batch entry."
                                        maxWidth={260}
                                    >
                                        <button
                                            className={checkPerm(Resources.TrucksDeduction, READ_PERM)
                                                ? "btn btn-outline flex items-center gap-1.5"
                                                : "focus:outline-hidden focus:ring-1 rounded-btn ring-red-600 p-2 text-tm-gray-500 cursor-default flex items-center gap-1.5"
                                            }
                                            onClick={() => checkPerm(Resources.TrucksDeduction, READ_PERM)
                                                ? setIsDeductionsDialogOpen(true)
                                                : dispatch(showGlobalModal('NoPermissionModal'))
                                            }
                                        >
                                            <EyeIcon className="w-4 h-4 shrink-0"/>
                                            Unprocessed deductions
                                        </button>
                                    </Tooltip>
                                </div>

                                <p className="text-xl flex items-center gap-1.5">
                                    Net position:
                                    {" "}
                                    <span
                                        className={
                                            classNames(
                                                "font-bold",
                                                grandTotal > 0 ? "text-green-600" : "text-red-600"
                                            )
                                        }
                                    >
                                        {genericMoneyFormatter(grandTotal)}
                                    </span>
                                    <Tooltip
                                        content="The current net balance between the truck and the company. Positive (green) means the company owes money to the truck. Negative (red) means the truck owes the company money."
                                        maxWidth={300}
                                    >
                                        <span>
                                            <InformationCircleIcon className="w-4 h-4 text-tm-gray-400 cursor-help shrink-0"/>
                                        </span>
                                    </Tooltip>
                                </p>
                            </div>
                        </div>
                    )}

                    <ModalDefault
                        show={activeModal === 'UnpaidItemsTotal'}
                        widthClass={'max-w-xl'}
                        title={translate("modal_heading.unpaid_items")}

                        closeButtonLabel={translate('Close')}
                        onClose={() => toggleTableModal(undefined)}
                    >
                        <div>
                            {!!data?.UnpaidItems?.length && (
                                <React.Fragment>
                                    {data?.UnpaidItems?.length > 10 && (
                                        <div className="px-6 py-2 max-w-xs mx-auto">
                                            <FieldSearch
                                                addClass={"form-control px-6"}
                                                onChange={(name, value) => setUnpaidItemsFilterValue(value)}
                                                value={unpaidItemsFilterValue}
                                            />
                                        </div>
                                    )}

                                    <ResourceTable
                                        data={paginatedUnpaidItemsTableData}

                                        fields={unpaidItemsFields}
                                        translate={translate}
                                        isLoading={isLoading}

                                        options={tableOptions}
                                    />
                                    {data?.UnpaidItems?.length > 10 && (
                                        <TableFooter
                                        >
                                            <Pagination
                                                count={unpaidItemsTableData.length ?? 0}
                                                queryFields={getQueryFields(unpaidItemsQueryValues)}
                                                handleQueryChange={handleUpdateUnpaidItemsOffset}
                                                translate={translate}
                                            />
                                        </TableFooter>
                                    )}
                                </React.Fragment>
                            )}

                            {!data?.UnpaidItems?.length && (
                                <div className="text-tm-gray-900 text-lg p-6 text-center">
                                    <h3 className="mt-2  font-medium text-primary">All settled</h3>
                                    <p className="mt-1 text-tm-gray-500">No open entries</p>
                                </div>
                            )}
                        </div>
                    </ModalDefault>

                    <ModalDefault
                        show={activeModal === 'EscrowBalancesTotal'}
                        widthClass={'max-w-xl'}
                        title={translate("modal_heading.escrow_balances_total")}

                        closeButtonLabel={translate('Close')}
                        onClose={() => toggleTableModal(undefined)}
                    >
                        <div>
                            {!!data?.EscrowBalances?.length && (
                                <React.Fragment>
                                    {data?.EscrowBalances?.length > 10 && (
                                        <div className="px-6 py-2 max-w-xs mx-auto">
                                            <FieldSearch
                                                addClass={"form-control px-6"}
                                                onChange={(name, value) => setEscrowBalancesFilterValue(value)}
                                                value={escrowBalancesFilterValue}
                                            />
                                        </div>
                                    )}

                                    <ResourceTable
                                        data={paginatedEscrowBalancesTableData}

                                        fields={escrowBalancesFields}
                                        translate={translate}
                                        isLoading={isLoading}

                                        options={tableOptions}
                                    />

                                    {data?.EscrowBalances?.length > 10 && (
                                        <TableFooter
                                        >
                                            <Pagination
                                                count={escrowBalancesTableData.length ?? 0}
                                                queryFields={getQueryFields(escrowBalancesQueryValues)}
                                                handleQueryChange={handleEscrowBalancesOffset}
                                                translate={translate}
                                            />
                                        </TableFooter>
                                    )}
                                </React.Fragment>
                            )}

                            {!data?.EscrowBalances?.length && (
                                <div className="text-tm-gray-900 text-lg p-6 text-center">
                                    <h3 className="mt-2  font-medium text-primary">All settled</h3>
                                    <p className="mt-1 text-tm-gray-500">No open entries</p>
                                </div>
                            )}
                        </div>
                    </ModalDefault>

                    <ModalDefault
                        show={activeModal === 'LoanBalancesTotal'}
                        widthClass={'max-w-md'}
                        title={translate("modal_heading.loan_balances_total")}

                        closeButtonLabel={translate('Close')}
                        onClose={() => toggleTableModal(undefined)}
                    >
                        <div>
                            {!!data?.LoanBalances?.length && (
                                <React.Fragment>
                                    {data?.LoanBalances?.length > 10 && (
                                        <div className="px-6 py-2 max-w-xs mx-auto">
                                            <FieldSearch
                                                addClass={"form-control px-6 rounded-0 w-full"}
                                                onChange={(name, value) => setLoanBalancesFilterValue(value)}
                                                value={loanBalancesFilterValue}
                                            />
                                        </div>
                                    )}

                                    <ResourceTable
                                        data={paginatedLoanBalancesTableData}

                                        fields={loanBalancesFields}
                                        translate={translate}
                                        isLoading={isLoading}

                                        options={tableOptions}
                                    />

                                    {data?.LoanBalances?.length > 10 && (
                                        <TableFooter
                                        >
                                            <Pagination
                                                count={loanBalancesTableData.length ?? 0}
                                                queryFields={getQueryFields(loanBalancesQueryValues)}
                                                handleQueryChange={handleLoanBalancesOffset}
                                                translate={translate}
                                            />
                                        </TableFooter>
                                    )}
                                </React.Fragment>
                            )}

                            {!data?.LoanBalances?.length && (
                                <div className="text-tm-gray-900 text-lg p-6 text-center">
                                    <h3 className="mt-2 font-medium text-primary">All settled</h3>
                                    <p className="mt-1 text-tm-gray-500">No open entries</p>
                                </div>
                            )}
                        </div>
                    </ModalDefault>

                    {!hasOpenEntries && (
                        <div className="max-w-xl mx-auto">
                            <Card bodyClass="p-6 text-center">
                                <div className="text-tm-gray-900 text-lg">
                                    <h3 className="mt-2  font-medium text-primary">All settled</h3>
                                    <p className="mt-1 text-tm-gray-500">No open entries</p>
                                </div>
                            </Card>
                        </div>
                    )}

                    <AdvancesDialog
                        resourceName={Resources.AccountingAccountAdvances}
                        show={isContactAdvancesDialogOpen}
                        onClose={() => setIsContactAdvancesDialogOpen(false)}
                        title="Unprocessed bills"
                        forceShowOnlyBillAdvances={true}
                        closeButtonLabel={translate('btn.Close')}
                        TruckID={TruckID}
                        AdvanceValue={getProp(data, "LoanBalancesTotal", 0).toFixed(2)}
                        expenseModalResource={advancesResource}
                        expenseModalOnFetchData={(selectedItem) => {
                            dispatch(getInfoResource({
                                user: LocalStorage.get('user'),
                                query: {
                                    ExpenseID: selectedItem?.ExpenseID
                                },
                                resource: Resources.ExpenseExpense
                            }))
                        }}
                        translate={translate}
                    />

                    <ResourceTableDialog
                        widthClass={'max-w-6xl'}
                        show={isReimbursementsDialogOpen}
                        dialogResource={dialogResource}
                        resource={Resources.AccountingReimbursement}
                        title="Unprocessed reimbursements"
                        dispatch={dispatch}
                        isFooterVisible={true}
                        sort="DESC"
                        sortBy="Date"
                        limit={100}
                        fields={{
                            Date: new Field('Date', '', [], false, 'date'),
                            Notes: new Field('Notes', '', [], false, 'text'),
                            Amount: new Field('Amount', '', [], false, 'money'),
                        }}
                        fieldsFilter={{
                            query: new Field('query', '', [], false, 'search', {addContainerClass: 'col-span-2'}),
                        }}
                        defaultQuery={{TruckID: TruckID ?? ""}}
                        modifyQuery={(query) => ({
                            ...query,
                            Open: 1,
                            searchFields: JSON.stringify({
                                Deduction: ["Deduction", "=", "1"],
                                TruckID: TruckID ?? "",
                                LoadID: ""
                            })
                        })}
                        closeButtonLabel={translate('btn.Close')}
                        onClose={() => setIsReimbursementsDialogOpen(false)}
                        translate={translate}
                    />

                    <ResourceTableDialog
                        widthClass={'max-w-6xl'}
                        show={isAdvancesDialogOpen}
                        dialogResource={dialogResource}
                        resource={Resources.AccountingReimbursement}
                        title="Unprocessed advances"
                        dispatch={dispatch}
                        isFooterVisible={true}
                        sort="DESC"
                        sortBy="Date"
                        limit={100}
                        fields={{
                            Date: new Field('Date', '', [], false, 'date'),
                            Notes: new Field('Notes', '', [], false, 'text'),
                            Amount: new Field('Amount', '', [], false, 'money'),
                        }}
                        fieldsFilter={{
                            query: new Field('query', '', [], false, 'search', {addContainerClass: 'col-span-2'}),
                        }}
                        defaultQuery={{TruckID: TruckID ?? ""}}
                        modifyQuery={(query) => ({
                            ...query,
                            Open: 1,
                            searchFields: JSON.stringify({
                                Deduction: ["Deduction", "=", "0"],
                                TruckID: TruckID ?? "",
                                LoadID: ""
                            })
                        })}
                        closeButtonLabel={translate('btn.Close')}
                        onClose={() => setIsAdvancesDialogOpen(false)}
                        translate={translate}
                    />

                    <ResourceTableDialog
                        widthClass={'max-w-6xl'}
                        show={isDeductionsDialogOpen}
                        dialogResource={dialogResource}
                        resource={Resources.TrucksDeduction}
                        title="Unprocessed deductions"
                        dispatch={dispatch}
                        isFooterVisible={true}
                        sort="DESC"
                        sortBy="StartDate"
                        limit={200}
                        fields={{
                            StartDate: new Field('StartDate', '', [], false, 'date'),
                            Notes: new Field('Notes', '', [], false, 'text'),
                            Deduction: new Field('Deduction', '', [], false, 'text', {
                                render: (it) => !it.Deduction
                                    ? <div className="flex gap-x-2">
                                        <PlusCircleIcon className="text-green-600 w-5 h-5"/>
                                        {translate("text.addition")}
                                    </div>
                                    : <div className="flex gap-x-2">
                                        <MinusCircleIcon className="text-red-600 w-5 h-5"/>
                                        {translate("text.deduction")}
                                    </div>
                            }),
                            Amount: new Field('Amount', '', [], false, 'float', {
                                render: (it) => genericMoneyFormatter(it?.Amount > 0 ? it?.Amount : it?.AmountPer)
                            }),
                        }}
                        fieldsFilter={{
                            query: new Field('query', '', [], false, 'search', {addContainerClass: 'col-span-2'}),
                        }}
                        id={TruckID}
                        defaultQuery={{NotUsed: 1}}
                        modifyQuery={(query) => ({
                            ...query,
                            searchFields: JSON.stringify({
                                DeductionRepeatTypeID: 4,
                                Active: 1
                            })
                        })}
                        closeButtonLabel={translate('btn.Close')}
                        onClose={() => setIsDeductionsDialogOpen(false)}
                        translate={translate}
                    />
                </React.Fragment>
            )}
        </div>
    )
}
